import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, currency } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Package, Car, AlertTriangle, TrendingUp, ArrowUpRight } from "lucide-react";

function Metric({ label, value, accent, testid, hint, to }) {
  const inner = (
    <>
      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">
        {label}
      </div>
      <div
        className={`font-heading text-4xl tracking-tighter font-black mt-3 ${accent || "text-zinc-950"}`}
      >
        {value}
      </div>
      {hint && <div className="text-xs text-zinc-500 mt-2">{hint}</div>}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className="bg-white border border-zinc-200 rounded-sm p-6 hover:border-zinc-950 hover:shadow-sm transition-all duration-150 block group"
      >
        {inner}
        <div className="mt-3 text-[10px] tracking-[0.2em] uppercase font-bold text-[#002FA7] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Open <ArrowUpRight className="w-3 h-3" />
        </div>
      </Link>
    );
  }
  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-6 hover:border-zinc-400 transition-colors duration-150"
      data-testid={testid}
    >
      {inner}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [recentVehicles, setRecentVehicles] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, ls, vs] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/parts", { params: { low_stock: true } }),
        api.get("/vehicles"),
      ]);
      setStats(s.data);
      setLowStock(ls.data);
      setRecentVehicles(vs.data.slice(0, 5));
    })();
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-7xl">
      <PageHeader
        eyebrow="Overview"
        title="Dashboard"
        description="Real-time snapshot of your parts inventory, vehicle stock, and profitability."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Metric
          label="Total Parts"
          value={stats?.total_parts ?? "—"}
          testid="metric-total-parts"
          hint={`${currency(stats?.inventory_value ?? 0)} inventory value`}
          to="/parts"
        />
        <Metric
          label="Low Stock Alerts"
          value={stats?.low_stock_count ?? "—"}
          accent={stats?.low_stock_count > 0 ? "text-[#FF2400]" : "text-zinc-950"}
          testid="metric-low-stock"
          hint={stats?.low_stock_count ? "Needs reorder" : "All stocked"}
          to="/alerts"
        />
        <Metric
          label="Vehicles"
          value={stats?.total_vehicles ?? "—"}
          testid="metric-vehicles"
          hint={`${stats?.vehicles_in_inventory ?? 0} in stock / ${stats?.vehicles_sold ?? 0} sold`}
          to="/vehicles"
        />
        {isAdmin && (
          <Metric
            label="Total Profit (Sold)"
            value={currency(stats?.total_profit ?? 0)}
            accent={(stats?.total_profit ?? 0) >= 0 ? "text-[#10B981]" : "text-[#FF2400]"}
            testid="metric-profit"
            hint="Across sold vehicles"
            to="/vehicles"
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <section className="bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FF2400]" />
              <h2 className="font-heading text-lg font-bold text-zinc-950">Reorder Alerts</h2>
            </div>
            <Link
              to="/alerts"
              className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1"
              data-testid="view-all-alerts"
            >
              View all <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-zinc-200">
            {lowStock.length === 0 && (
              <div className="px-6 py-8 text-sm text-zinc-500 flex items-center gap-2">
                <Package className="w-4 h-4" /> All parts are sufficiently stocked.
              </div>
            )}
            {lowStock.slice(0, 6).map((p) => (
              <div
                key={p.id}
                className="px-6 py-3 flex items-center justify-between hover:bg-zinc-50"
                data-testid={`alert-row-${p.sku}`}
              >
                <div>
                  <div className="text-sm font-semibold text-zinc-950">{p.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{p.sku}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono text-[#FF2400] font-bold">
                    {p.quantity} / {p.min_stock}
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                    In stock / Min
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-zinc-700" />
              <h2 className="font-heading text-lg font-bold text-zinc-950">Recent Vehicles</h2>
            </div>
            <Link
              to="/vehicles"
              className="text-xs font-semibold text-[#002FA7] hover:underline flex items-center gap-1"
              data-testid="view-all-vehicles"
            >
              View all <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-zinc-200">
            {recentVehicles.length === 0 && (
              <div className="px-6 py-8 text-sm text-zinc-500 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> No vehicles yet. Add one to start tracking.
              </div>
            )}
            {recentVehicles.map((v) => (
              <Link
                key={v.id}
                to={`/vehicles/${v.id}`}
                className="px-6 py-3 flex items-center justify-between hover:bg-zinc-50 block"
                data-testid={`dashboard-vehicle-${v.id}`}
              >
                <div>
                  <div className="text-sm font-semibold text-zinc-950">
                    {v.year} {v.make} {v.model}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">{v.vin}</div>
                </div>
                <div className="text-right">
                  <div
                    className={`text-sm font-mono font-bold ${
                      v.profit >= 0 ? "text-[#10B981]" : "text-[#FF2400]"
                    }`}
                  >
                    {currency(v.profit)}
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                    {v.status === "sold" ? "Profit" : "Running"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
